import type { InstrumentRef, SituationView, WrappedCoin } from "@screener/protocol";
import { memo, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { SituationRow as SituationRowData } from "./situation-derive";
import { SituationRow } from "./situation-row";

/**
 * Мемоизированная строка группы «Ситуаций» (design.md stage9-render-scope,
 * решение 3). Колбэки приходят от списка постоянными ссылками и адресуются
 * ключом строки или самой строкой; замыкания для `SituationRow` строит
 * обёртка. Ряд пересобирается при каждом пересчёте новыми объектами, поэтому
 * сравнение идёт по содержимому, а не по ссылке на ряд.
 */
export interface SituationRowItemProps {
  row: SituationRowData;
  wrapped: readonly WrappedCoin[];
  exchangeBases: ReadonlyMap<string, string>;
  expanded: boolean;
  focusable: boolean;
  onFocus: (rowKey: string) => void;
  onKeyDown: (rowKey: string, event: ReactKeyboardEvent<HTMLTableRowElement>) => void;
  onToggleExpand: (row: SituationRowData) => void;
}

function sameInstrument(left: InstrumentRef, right: InstrumentRef): boolean {
  return (
    left === right ||
    (left.exchange === right.exchange &&
      left.market === right.market &&
      left.base === right.base &&
      left.quote === right.quote)
  );
}

/**
 * Поля, которые сравнение знает. Списки читает и само сравнение, и тест
 * `situation-row-item.test.tsx`: новое поле в generated-типе роняет тест,
 * а не даёт строке тихо застыть.
 */
export const SITUATION_FIELDS = ["key", "kind", "members", "aggregate"] as const;
export const SITUATION_KEY_FIELDS = ["base", "quote", "side", "exchange"] as const;
export const SITUATION_AGGREGATE_FIELDS = [
  "total_usd",
  "markets",
  "first_seen_min_ms",
  "first_seen_max_ms",
  "approaches_count",
] as const;
/** Скалярные поля участника ситуации; `instrument` сравнивается отдельно. */
export const SITUATION_MEMBER_SCALAR_FIELDS = [
  "density_id",
  "search_density_id",
  "group_id",
] as const;
export const SITUATION_MEMBER_FIELDS = [...SITUATION_MEMBER_SCALAR_FIELDS, "instrument"] as const;
export const SITUATION_ROW_FIELDS = [
  "key",
  "memberIds",
  "situation",
  "members",
  "knownCount",
  "hiddenCount",
  "anchorMin",
  "anchorMax",
  "nearest",
  "leading",
] as const;

function sameFields<T extends object>(left: T, right: T, fields: readonly (keyof T)[]): boolean {
  return fields.every((field) => left[field] === right[field]);
}

/** Ситуация §5 по содержимому: после `situations` с тем же составом ссылки новые, значения прежние. */
export function sameSituation(left: SituationView, right: SituationView): boolean {
  if (left === right) {
    return true;
  }
  return (
    left.kind === right.kind &&
    sameFields(left.key, right.key, SITUATION_KEY_FIELDS) &&
    sameFields(left.aggregate, right.aggregate, SITUATION_AGGREGATE_FIELDS) &&
    left.members.length === right.members.length &&
    left.members.every((member, index) => {
      const other = right.members[index];
      return (
        other !== undefined &&
        sameFields(member, other, SITUATION_MEMBER_SCALAR_FIELDS) &&
        sameInstrument(member.instrument, other.instrument)
      );
    })
  );
}

function sameNearest(
  left: SituationRowData["nearest"],
  right: SituationRowData["nearest"],
): boolean {
  if (left === null || right === null) {
    return left === right;
  }
  return (
    left.distancePct === right.distancePct && sameInstrument(left.instrument, right.instrument)
  );
}

/** Ряд списка по содержимому: видимые участники — снимками по ссылке, всё остальное — по значению. */
export function sameSituationRow(left: SituationRowData, right: SituationRowData): boolean {
  if (left === right) {
    return true;
  }
  return (
    left.key === right.key &&
    left.knownCount === right.knownCount &&
    left.hiddenCount === right.hiddenCount &&
    left.anchorMin === right.anchorMin &&
    left.anchorMax === right.anchorMax &&
    sameNearest(left.nearest, right.nearest) &&
    sameInstrument(left.leading, right.leading) &&
    left.memberIds.length === right.memberIds.length &&
    left.memberIds.every((id, index) => id === right.memberIds[index]) &&
    left.members.length === right.members.length &&
    left.members.every((member, index) => member === right.members[index]) &&
    sameSituation(left.situation, right.situation)
  );
}

/** Сколько пропсов знает `areSituationRowItemPropsEqual`: лишний ключ не даёт строке застыть. */
const KNOWN_PROP_COUNT = 8;

export function areSituationRowItemPropsEqual(
  previous: SituationRowItemProps,
  next: SituationRowItemProps,
): boolean {
  return (
    Object.keys(previous).length === KNOWN_PROP_COUNT &&
    Object.keys(next).length === KNOWN_PROP_COUNT &&
    sameSituationRow(previous.row, next.row) &&
    previous.wrapped === next.wrapped &&
    previous.exchangeBases === next.exchangeBases &&
    previous.expanded === next.expanded &&
    previous.focusable === next.focusable &&
    previous.onFocus === next.onFocus &&
    previous.onKeyDown === next.onKeyDown &&
    previous.onToggleExpand === next.onToggleExpand
  );
}

function SituationRowItemView({
  row,
  wrapped,
  exchangeBases,
  expanded,
  focusable,
  onFocus,
  onKeyDown,
  onToggleExpand,
}: SituationRowItemProps) {
  return (
    <SituationRow
      row={row}
      wrapped={wrapped}
      exchangeBases={exchangeBases}
      expanded={expanded}
      focusable={focusable}
      onFocus={() => onFocus(row.key)}
      onKeyDown={(event) => onKeyDown(row.key, event)}
      onToggle={() => onToggleExpand(row)}
    />
  );
}

export const SituationRowItem = memo(SituationRowItemView, areSituationRowItemPropsEqual);
